import { useEffect, useRef, useState } from "react";
import {
  ModalControlData,
  useModalControl,
  useRequest,
} from "../hooks/commonHooks";
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItemProps,
  Form,
  FormProps,
  Input,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { httpRequest, restRequest } from "../http";
import { notificationControl } from "../controls/notification-control";
import _ from "lodash";
import $ from "jquery";
import { dialogControl } from "../controls/dialog-control";
import { Bin } from "d3";
import { fieldError, formatDateTime } from "../utils";
import { PageData } from "../models/common_model";
import { useCurrentUser } from "../hooks/auth-hook";
import { Page } from "./commons/Page";
import { RefurbishmentSerialNumberData } from "../models/refurbishment_model";

type BindError = {
  serial_number: string;
  re_serial_number?: string;
  error: string;
  actions: string[];
};

export function RefurbishmentComponent(props: {}) {
  const [searchData, setSearchField] = useFormData<{
    product: string;
    serial_number: string;
    no_testing_data: boolean;
    refresh: number;
  }>({ product: "", serial_number: "", no_testing_data: false, refresh: 0 });
  const [refurbishmentSerials, setSearchData] = useFormConnect<
    any,
    PageData<RefurbishmentSerialNumberData>
  >({}, "refurbishments/search");
  const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
  const [file, setFile] = useState<any>();
  const [loading, setLoading] = useState(false);
  const editModalControl = useModalControl(0);
  const currentUser = useCurrentUser();

  const [allProducts, loadingAllProducts] = useRequest<string[]>(
    "testings/all_products",
    null,
    "get"
  );

  useEffect(() => {
    if (allProducts) {
      const options = [
        { key: "", text: "All", value: "" },
        ...allProducts.map((p) => ({ key: p, text: p, value: p })),
      ];
      setProductOptions(options);
    }
  }, [allProducts]);

  return (
    <>
      <Segment.Group>
        <Segment attached="top">
          <Form onSubmit={(e) => setSearchData({...searchData, refresh: searchData.refresh + 1})} loading={loading}>
            <Form.Group>
              <Form.Field inline>
                <Input
                  placeholder="Serial Number"
                  name="serial_number"
                  value={searchData.serial_number || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Input                
                inline
                label="Product"
                style={{ minWidth: "10rem" }}
                loading={loadingAllProducts}
              >
                <Dropdown
                  name="product"
                  value={searchData.product || ""}
                  onChange={setSearchField}
                  options={productOptions}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>

              <Form.Field>
                <Button as="a" icon="check" content="No Testing Data" secondary={searchData.no_testing_data} onClick={e => setSearchField(null, {name: 'no_testing_data', value: !searchData.no_testing_data})}></Button>
              </Form.Field>

              <div>
                <Button icon="search" type="submit"></Button>
              </div>
            </Form.Group>
          </Form>
        </Segment>
        <Segment attached="bottom">
          <Table striped>
            <Table.Header>
              <Table.Row>
                <th>Serial</th>
                <th>Refurbishment Serial</th>
                <th>Product</th>
                <th>Order</th>
                <th>Serial Name</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>
                  {currentUser.permissions.refurbishment.create && (
                    <Button
                      color="blue"
                      icon="add"
                      content="Refurbishment"
                      size="mini"
                      onClick={(e) => {
                        editModalControl.setOpen(true);
                      }}
                    />
                  )}
                </th>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {refurbishmentSerials?.items.map((obj) => {
                return (
                  <tr key={obj.serial_number}>
                    <td>{obj.serial_number}</td>
                    <td>{obj.re_serial_number}</td>
                    <td>{obj.product}</td>
                    <td>{obj.order}</td>
                    <td>{obj.name || 'MIC'}</td>
                    <td>{obj.created_by_name}</td>
                    <td>{formatDateTime(obj.created_at)}</td>
                    <td>
                      {currentUser.permissions.refurbishment.delete && <Button
                        onClick={(e) => {
                          e.preventDefault();
                          dialogControl.deletionConfirm(`${obj.serial_number} -> ${obj.re_serial_number}`, agree => {
                            if (agree) {
                              return restRequest('/refurbishments/delete', {serial_number: obj.serial_number}, 'post').then(() => {
                                setSearchData((searchData: any) => ({
                                  ...searchData,
                                  refresh: searchData.refresh + 1,
                                }));
                              });
                            }

                            return Promise.resolve();
                          })
                        }}
                        color="red"
                        icon="delete"
                        size="mini"
                      />}
                    </td>
                  </tr>
                );
              })}
            </Table.Body>
          </Table>
          <Page data={refurbishmentSerials} updatePage={setSearchData}></Page>
        </Segment>
      </Segment.Group>

      <Editor
        modalControl={editModalControl}
        onFinish={() => {
          editModalControl.data += 1;
          editModalControl.setOpen(false);
          setSearchData((searchData: any) => ({
            ...searchData,
            refresh: (searchData.refresh || 1) + 1,
          }));
        }}
      />
    </>
  );
}

function EditorSerialErrors(props: {
  modalControl: ModalControlData<BindError[]>;
}) {
  const errorSerials = props.modalControl.data;
  return (
    <>
      <Modal {...props.modalControl.props} size="fullscreen">
        <Modal.Header> Some Serials Are Failed to Add </Modal.Header>
        <Modal.Content>
          {!_.isEmpty(errorSerials) && (
            <Table striped>
              <Table.Header>
                <Table.Row>
                  <th>Serial Number</th>
                  <th>Refurbishment Serial Number</th>
                  <th>Error</th>
                  <th>Operation</th>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {errorSerials
                  .sort((a, b) => {
                    if (a.actions && !b.actions) {
                      return -1;
                    } else if (!a.actions && b.actions) {
                      return 1;
                    }

                    return 0;
                  })
                  .map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td>{row.serial_number}</td>
                      <td>{row.re_serial_number || ""}</td>
                      <td>{row.error}</td>
                      <td>
                        {row.actions &&
                          row.actions.map((action, i) => {
                            if (action == "force_update") {
                              return (
                                <Button
                                  key={i}
                                  size="mini"
                                  compact
                                  onClick={(e) => {
                                    dialogControl.confirm(
                                      "Are you sure to force update which will overwrite the old one?",
                                      (agree) => {
                                        if (agree) {
                                          restRequest(
                                            "refurbishments/force_update",
                                            {
                                              serial_number: row.serial_number,
                                              re_serial_number:
                                                row.re_serial_number,
                                            },
                                            "post"
                                          ).then((v) => {
                                            $(e.target).closest("tr").fadeOut();
                                          });
                                        }

                                        return Promise.resolve();
                                      }
                                    );
                                  }}
                                >
                                  Force Update
                                </Button>
                              );
                            }
                          })}
                      </td>
                    </tr>
                  ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={(e) => {
              e.preventDefault();
              props.modalControl.setOpen(false);
            }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

function Editor(props: {
  modalControl: ModalControlData<number>;
  onFinish: () => void;
}) {
  const { modalControl, onFinish } = props;
  const [invalidError, setInvalidError] = useState<any>({});

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<any>();
  const bindErrorModalControl = useModalControl<BindError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [modalControl.data]);

  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setLoading(true);

    const formData = new FormData();

    if (file) {
      formData.append("csv_file", file);
    }

    httpRequest<{ errors: BindError[] }>(
      "refurbishments/create",
      formData,
      "post",
      {}
    )
      .then(
        (result) => {
          setInvalidError({});

          if (_.isEmpty(result.errors)) {
            notificationControl.showSuccess("all Serial Numbers bind success");
            onFinish();
          } else {
            bindErrorModalControl.data = result.errors;
            bindErrorModalControl.setOpen(true);
          }
        },
        (resp) => {
          if (resp.status === 2) {
            setInvalidError(resp.data);
          }
        }
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Modal {...modalControl.props} size="large">
        <Modal.Header> Add Refurbishment Serial Numbers </Modal.Header>
        <Modal.Content>
          <Form onSubmit={onSubmit} loading={loading}>
            <Message>
              The CSV must be: the first column is Serial Number, while the
              second column is Refurbishment Serial Number
            </Message>
            <Form.Group widths="equal">
              <Form.Input
                label="CSV File"
                required
                error={fieldError("csv_file", invalidError)}
              >
                <input
                  type="file"
                  name="csv_file"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setFile(e.target.files[0]);
                    }
                  }}
                  ref={fileInputRef}
                />
              </Form.Input>
            </Form.Group>
            <div>
              <Button primary>Submit</Button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button as="a" onClick={(e) => modalControl.setOpen(false)}>
                Close
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>
      <EditorSerialErrors modalControl={bindErrorModalControl}></EditorSerialErrors>
    </>
  );
}
